import cn from "classnames";
import { Typography } from "ui-v2/components/Typography";
import { TypographyVariantsTypes } from "ui-v2/types/typography";
import s from "./RatingTag.module.scss";
import Icon from "../Icon/Icon";
import React from "react";

enum RatingTagSize {
  s = "s",
  m = "m",
  l = "l",
  xl = "xl",
}

type RatingTagSizeType = keyof typeof RatingTagSize;

export type RatingTagProps = {
  rating: number;
  ratingCount?: number;
  ratingCountText?: string;
  size: RatingTagSizeType;
  disabled?: boolean;
};

/**
 * RatingTag component for displaying rating with total number of ratings
 *
 * @param rating - mandatory , average rating value
 * @param ratingCount - optional, by default 0, to display total number of ratings
 * @param ratingCountText - optional, by default empty string, to display custom text with reviews
 * @param size - mandatory, size of the rating tag, has variants : "s" | "m" |  "l" | xl
 * @param disabled - optional, by default false, to disable the rating tag
 */
export function RatingTag({
  rating,
  ratingCount = 0,
  ratingCountText = "",
  size,
  disabled = false,
}: RatingTagProps) {
  const ratingTitleVariants: Record<
    string,
    { title: TypographyVariantsTypes; subtitle: TypographyVariantsTypes }
  > = {
    s: { title: "tiny_bold", subtitle: "tiny_medium" },
    m: { title: "label_3_bold", subtitle: "label_3_medium" },
    l: { title: "h_2_bold", subtitle: "h_2_medium" },
    xl: { title: "title_2_bold", subtitle: "title_2_medium" },
  };

  const heightWidth: Record<string, { height: number; width: number }> = {
    s: { height: 12, width: 12 },
    m: { height: 18, width: 18 },
    l: { height: 26, width: 26 },
    xl: { height: 32, width: 32 },
  };

  return (
    <div className={s.ratingTag}>
      <div
        className={cn(
          s[size],
          s[`star_${Math.floor(rating)}`],
          s.contentStack,
          { [s.disabled]: disabled }
        )}
      >
        <Icon
          name="StarCircleBold"
          height={heightWidth[size].height}
          width={heightWidth[size].width}
        />
        {!!rating && (
          <Typography
            label={`${Number(rating)?.toFixed(1)}`}
            variant={ratingTitleVariants[size].title}
            color={"primary"}
            className={s.rating}
          />
        )}

        {!!ratingCount && (
          <Typography
            label={`(${ratingCountText || ratingCount})`}
            variant={ratingTitleVariants[size].subtitle}
            color={"secondary"}
            className={s.rating}
          />
        )}
      </div>
    </div>
  );
}
